import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

export type ConcernType = "risk" | "assumption" | "issue" | "decision";
export type Severity = "low" | "medium" | "high";

export interface Concern {
  id: number;
  /** Exactly one of project_id / initiative_id is set, matching the
   *  endpoint the row came from. */
  project_id?: string | null;
  initiative_id?: string | null;
  type: ConcernType;
  title: string;
  body?: string | null;
  severity?: Severity | null;
  status: string;
  owner?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  resolved_at?: string | null;
  /** Spreadsheet import (2026-10-02): the file's ID and the columns the
   *  form has no field for. */
  external_ref?: string | null;
  category?: string | null;
  source?: string | null;
  raised_on?: string | null;
}

export type ConcernParentType = "project";

/** API prefix per parent. */
function _prefix(_parent: ConcernParentType): string {
  return "/concerns";
}

export interface ConcernCreate {
  type: ConcernType;
  title: string;
  body?: string | null;
  severity?: Severity | null;
  status?: string | null;
  owner?: string | null;
}

export interface ConcernUpdate {
  type?: ConcernType;
  title?: string;
  body?: string | null;
  severity?: Severity | null;
  status?: string | null;
  owner?: string | null;
}

// Each hook accepts an optional parentType (default "project") so
// existing call sites keep working without any changes.

export function useConcerns(
  parentId: string | null,
  type?: ConcernType,
  parentType: ConcernParentType = "project",
) {
  const KEY = _prefix(parentType);
  return useQuery<Concern[]>({
    queryKey: [KEY, parentId, type ?? "all"],
    queryFn: () =>
      api
        .get(`${KEY}/${parentId}`, { params: type ? { type } : undefined })
        .then((r) => r.data),
    enabled: !!parentId,
  });
}

export function useAddConcern(
  parentId: string,
  parentType: ConcernParentType = "project",
) {
  const KEY = _prefix(parentType);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ConcernCreate) =>
      api.post(`${KEY}/${parentId}`, payload).then((r) => r.data as Concern),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, parentId] });
    },
  });
}

export function useUpdateConcern(
  parentId: string,
  parentType: ConcernParentType = "project",
) {
  const KEY = _prefix(parentType);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: ConcernUpdate }) =>
      api.patch(`${KEY}/id/${id}`, patch).then((r) => r.data as Concern),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, parentId] });
    },
  });
}

export function useDeleteConcern(
  parentId: string,
  parentType: ConcernParentType = "project",
) {
  const KEY = _prefix(parentType);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`${KEY}/id/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY, parentId] });
    },
  });
}


// ---------------------------------------------------------------------------
// Spreadsheet import (Brett, 2026-10-02): preview, then commit. Projects only.
// ---------------------------------------------------------------------------

export interface RaidImportRow {
  row: number;
  ref: string | null;
  action: "create" | "update" | "skip";
  reason: string | null;
  type: ConcernType | null;
  title: string | null;
  status: string | null;
}

export interface RaidImportResult {
  rows: RaidImportRow[];
  counts: { create: number; update: number; skip: number };
  applied: boolean;
  created: number;
  updated: number;
  skipped: number;
}

export function useImportConcerns(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, commit }: { file: File; commit: boolean }) => {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post(`/concerns/${projectId}/import`, form, { params: { commit } });
      return r.data as RaidImportResult;
    },
    onSuccess: (data) => {
      if (data.applied) qc.invalidateQueries({ queryKey: ["/concerns", projectId] });
    },
  });
}

/** Fetches the blank template through the authenticated client and hands
 *  it to the browser as a download. */
export async function downloadRaidTemplate(projectId?: string): Promise<void> {
  const r = await api.get("/concerns/import/template", { params: projectId ? { project_id: projectId } : undefined, responseType: "blob" });
  const url = URL.createObjectURL(r.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `RAID_Log_Template${projectId ? "_" + projectId : ""}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
